/** Lesson summary (STORY-058): the facts the log established, the people who struggled (named
 * from the roster), and an AI-drafted reflection the teacher edits and keeps as versions. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { TeacherChrome } from "../teacher/Chrome";
import {
  generateSummary, getAllocation, getCourse, getFacts, getRoster, getSession, getSummary, isOfflineError, saveSummary, scheduleRecap, summaryGeneration,
  type SummaryBody, type SummaryOut,
} from "../teacher/api";
import { Button, DraftChip } from "../ui";

const pct = (x: number | null) => (x === null ? "–" : `${Math.round(x * 100)}%`);

export function Summary() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const { t } = useTranslation();
  const qc = useQueryClient();
  const session = useQuery({ queryKey: ["session", sessionId], queryFn: () => getSession(sessionId) });
  const courseId = session.data?.course_id;
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId!), enabled: !!courseId });
  const roster = useQuery({ queryKey: ["roster", courseId], queryFn: () => getRoster(courseId!), enabled: !!courseId });
  const facts = useQuery({ queryKey: ["facts", sessionId], queryFn: () => getFacts(sessionId), retry: false });
  const latest = useQuery({ queryKey: ["summary", sessionId], queryFn: () => getSummary(sessionId), retry: false });
  const [editing, setEditing] = useState<SummaryBody | null>(null);
  const [base, setBase] = useState<SummaryOut | null>(null);
  const [gen, setGen] = useState<{ job: string | null; error: string | null }>({ job: null, error: null });
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const allocation = useQuery({ queryKey: ["allocation", courseId], queryFn: () => getAllocation(courseId!), enabled: !!courseId });
  const today = new Date().toISOString().slice(0, 10);
  const futureLessons = (allocation.data?.lessons ?? []).filter((l) => l.date >= today);
  const [recapLesson, setRecapLesson] = useState<Record<string, string>>({});
  const [recapDone, setRecapDone] = useState<Record<string, string>>({});
  const recap = useMutation({
    mutationFn: ({ loId, lessonId, note }: { loId: string; lessonId: string; note: string }) =>
      scheduleRecap(lessonId, { lo_id: loId, note, source_session_id: sessionId }),
    onSuccess: (plan, vars) => setRecapDone((d) => ({ ...d, [vars.loId]: plan.lesson_id })),
  });

  useEffect(() => {
    if (!editing && latest.data) {
      setBase(latest.data);
      setEditing(structuredClone(latest.data.summary));
    }
  }, [editing, latest.data]);

  useEffect(() => {
    if (!gen.job) return;
    const job = gen.job;
    const id = window.setInterval(async () => {
      try {
        const g = await summaryGeneration(sessionId, job);
        if (g.status === "done" || g.status === "failed") {
          window.clearInterval(id);
          setGen({ job: null, error: g.status === "failed" ? (g.error ?? "failed") : null });
          await qc.invalidateQueries({ queryKey: ["summary", sessionId] });
          if (g.summary) {
            setBase(g.summary);
            setEditing(structuredClone(g.summary.summary));
          }
        }
      } catch (e) {
        window.clearInterval(id);
        setGen({ job: null, error: String(e) });
      }
    }, 2500);
    return () => window.clearInterval(id);
  }, [gen.job, sessionId, qc]);

  const generate = useMutation({
    mutationFn: () => generateSummary(sessionId),
    onSuccess: (g) => setGen({ job: g.job_id, error: null }),
    onError: (e) => setGen({ job: null, error: e instanceof ApiError && e.code === "no_evidence" ? t("summary.noFacts") : String(e) }),
  });
  const save = useMutation({
    mutationFn: (body: SummaryBody) => saveSummary(sessionId, body),
    onSuccess: (s) => {
      setBase(s);
      setEditing(structuredClone(s.summary));
      setSavedMsg(t("summary.saved", { n: s.version }));
      void qc.invalidateQueries({ queryKey: ["summary", sessionId] });
    },
  });

  const name = (personId: string) => roster.data?.find((p) => p.person_id === personId)?.label || personId.slice(0, 6);
  const loCode = (id: string) => facts.data?.los.find((l) => l.lo_id === id)?.code ?? id;
  const dirty = !!editing && (!base || JSON.stringify(editing) !== JSON.stringify(base.summary));
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  const notProjected = facts.error instanceof ApiError && facts.error.status === 404;
  const needsServer = isOfflineError(facts.error);
  const blank = (): SummaryBody => ({ headline: "", went_well: [], revisit: [], recap_suggestion: "", notes: "" });
  const setList = (key: "went_well", i: number, v: string) =>
    setEditing((e) => (e ? { ...e, [key]: e[key].map((x, j) => (j === i ? v : x)) } : e));

  return (
    <TeacherChrome title={t("summary.title")} course={course.data && courseId ? { id: courseId, title: course.data.title } : null} stage="live" wide>
      {/* The chrome prints the title; this header carries the way back and the lede only. */}
      <header className="-mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
        <Link to="/t/live/$sessionId" params={{ sessionId }} className="underline">{t("summary.backToLive")}</Link>
        <p className="measure text-[color:var(--ink-2)]">{t("summary.lede")}</p>
      </header>
      {notProjected && <p className={card}>{t("summary.notProjected")}</p>}
      {needsServer && <p className={card}>{t("dash.needsServer")}</p>}

      {facts.data && (
        <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
          <section className={`${card} flex flex-col gap-3`}>
            <h2 className="font-semibold">{t("summary.facts")}</h2>
            <p className="text-sm text-[color:var(--ink-2)]">{t("summary.participants", { count: facts.data.participants })}</p>
            {facts.data.los.length === 0 ? (
              <p className="text-sm">{t("summary.noFacts")}</p>
            ) : (
              <table className="text-sm">
                <thead className="text-left text-xs text-[color:var(--ink-2)]">
                  <tr>
                    <th scope="col" className="pr-2 font-medium">{t("summary.objective")}</th>
                    <th scope="col" className="px-1 font-medium">{t("summary.items")}</th>
                    <th scope="col" className="px-1 font-medium">{t("summary.first")}</th>
                    <th scope="col" className="px-1 font-medium">{t("summary.final")}</th>
                    <th scope="col" className="px-1 font-medium">{t("summary.delta")}</th>
                    <th scope="col" className="px-1 font-medium">{t("summary.secure")}</th>
                  </tr>
                </thead>
                <tbody>
                  {facts.data.los.map((lo) => (
                    <tr key={lo.lo_id} className="align-top">
                      <td className="pr-2"><span className="mr-1 font-mono text-xs">{lo.code}</span>{lo.text}</td>
                      <td className="bignum px-1 text-center">{lo.items}</td>
                      <td className="bignum px-1 text-center">{pct(lo.first_correct_share)}</td>
                      <td className="bignum px-1 text-center">{pct(lo.last_correct_share)}</td>
                      <td className="bignum px-1 text-center">{lo.revote_delta === null ? "–" : `${lo.revote_delta >= 0 ? "+" : ""}${Math.round(lo.revote_delta * 100)}`}</td>
                      <td className="bignum px-1 text-center">{lo.secure_before} → {lo.secure_after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div>
              <h3 className="text-sm font-medium">{t("summary.strugglers")}</h3>
              {facts.data.strugglers.length === 0 ? (
                <p className="text-sm text-[color:var(--ink-2)]">{t("summary.strugglersNone")}</p>
              ) : (
                <ul className="text-sm">
                  {facts.data.strugglers.map((s) => (
                    <li key={s.respondent_id}>{name(s.respondent_id)} <span className="text-xs text-[color:var(--ink-2)]">{t("summary.wrongOf", { wrong: s.wrong, answered: s.answered })}</span></li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className={`${card} flex flex-col gap-3`}>
            <div className="flex flex-wrap items-center gap-2">
              {base?.source === "ai" && <DraftChip />}
              {base && <span className="text-sm text-[color:var(--ink-2)]">{t("summary.version", { n: base.version })} · {base.source === "ai" ? t("summary.sourceAi") : t("summary.sourceTeacher")}</span>}
              <span className="ml-auto flex gap-2">
                <Button variant="secondary" loading={generate.isPending || !!gen.job} disabled={facts.data.los.length === 0} onClick={() => generate.mutate()}>
                  {gen.job ? t("summary.drafting") : t("summary.draft")}
                </Button>
                {editing && <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate(editing)}>{t("summary.save")}</Button>}
              </span>
            </div>
            {gen.error && <p role="alert" className="text-sm text-[color:var(--not-yet)]">{gen.error}</p>}
            {savedMsg && !dirty && <p className="text-sm text-[color:var(--secure)]">{savedMsg}</p>}
            {!editing && latest.isError && (
              <p className="text-sm text-[color:var(--ink-2)]">
                {t("summary.noDraft")}{" "}
                <button type="button" className="underline" onClick={() => setEditing(blank())}>{t("summary.add")}</button>
              </p>
            )}
            {editing && (
              <div className="flex flex-col gap-3 text-sm">
                <label className="flex flex-col gap-1">
                  <span className="font-medium">{t("summary.headline")}</span>
                  <input className="field" value={editing.headline} onChange={(e) => setEditing({ ...editing, headline: e.target.value })} />
                </label>
                <div className="flex flex-col gap-1">
                  <span className="font-medium">{t("summary.wentWell")}</span>
                  {editing.went_well.map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className="field min-w-0 flex-1" value={w} onChange={(e) => setList("went_well", i, e.target.value)} />
                      <Button variant="tertiary" aria-label={t("summary.remove")} onClick={() => setEditing({ ...editing, went_well: editing.went_well.filter((_, j) => j !== i) })}>×</Button>
                    </div>
                  ))}
                  <button type="button" className="self-start underline" onClick={() => setEditing({ ...editing, went_well: [...editing.went_well, ""] })}>{t("summary.add")}</button>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="font-medium">{t("summary.revisit")}</span>
                  {editing.revisit.map((r, i) => (
                    <div key={i} className="flex flex-col gap-1 rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-2">
                      <div className="flex items-center gap-2">
                        <select className="field min-w-0 flex-1" aria-label={t("summary.objective")} value={r.lo_id} onChange={(e) => setEditing({ ...editing, revisit: editing.revisit.map((x, j) => (j === i ? { ...x, lo_id: e.target.value } : x)) })}>
                          {facts.data!.los.map((lo) => <option key={lo.lo_id} value={lo.lo_id}>{lo.code} · {lo.text}</option>)}
                        </select>
                        <Button variant="tertiary" aria-label={t("summary.remove")} onClick={() => setEditing({ ...editing, revisit: editing.revisit.filter((_, j) => j !== i) })}>×</Button>
                      </div>
                      <input className="field" placeholder={t("summary.why")} aria-label={t("summary.why")} value={r.why} onChange={(e) => setEditing({ ...editing, revisit: editing.revisit.map((x, j) => (j === i ? { ...x, why: e.target.value } : x)) })} />
                      <input className="field" placeholder={t("summary.nextStep")} aria-label={t("summary.nextStep")} value={r.next_step} onChange={(e) => setEditing({ ...editing, revisit: editing.revisit.map((x, j) => (j === i ? { ...x, next_step: e.target.value } : x)) })} />
                      {futureLessons.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          {recapDone[r.lo_id] ? (
                            <Link to="/t/c/$courseId/lessons/$lessonId" params={{ courseId: courseId!, lessonId: recapDone[r.lo_id]! }} className="underline">{t("summary.recapDone")}</Link>
                          ) : (
                            <>
                              <select className="field field-sm min-w-0 max-w-[16rem]" aria-label={t("summary.recapInto")} value={recapLesson[r.lo_id] ?? futureLessons[0]!.lesson_id} onChange={(e) => setRecapLesson((m) => ({ ...m, [r.lo_id]: e.target.value }))}>
                                {futureLessons.map((l, k) => <option key={l.lesson_id} value={l.lesson_id}>{k + 1} · {l.date}{l.title ? ` · ${l.title}` : ""}</option>)}
                              </select>
                              <button type="button" className="underline" disabled={recap.isPending} onClick={() => recap.mutate({ loId: r.lo_id, lessonId: recapLesson[r.lo_id] ?? futureLessons[0]!.lesson_id, note: [r.why, r.next_step].filter(Boolean).join(" ") })}>{t("summary.recap")}</button>
                              <span className="text-[color:var(--ink-2)]">{t("summary.recapHint")}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {facts.data.los.length > 0 && (
                    <button type="button" className="self-start underline" onClick={() => setEditing({ ...editing, revisit: [...editing.revisit, { lo_id: facts.data!.los[0]!.lo_id, why: "", next_step: "" }] })}>{t("summary.add")}</button>
                  )}
                </div>
                <label className="flex flex-col gap-1">
                  <span className="font-medium">{t("summary.recap")}</span>
                  <textarea className="field" rows={3} value={editing.recap_suggestion} onChange={(e) => setEditing({ ...editing, recap_suggestion: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="font-medium">{t("summary.notes")}</span>
                  <textarea className="field" rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
                </label>
                {editing.revisit.length > 0 && <p className="text-xs text-[color:var(--ink-2)]">{editing.revisit.map((r) => loCode(r.lo_id)).join(", ")}</p>}
              </div>
            )}
          </section>
        </div>
      )}
    </TeacherChrome>
  );
}
